import { YearInReviewStatsQuery } from "@/__generated__/graphql";
import { UserProfile } from "@/__generated__/graphql";
import { Download, Share } from "@mui/icons-material";
import { Box, Button, Stack, Typography } from "@mui/joy";
import { FormattedMessage } from "react-intl";
import { useRef, useState } from "react";
import html2canvas from "html2canvas";

// Функция для определения количества слов в строке
const getWordCount = (text: string | number): number => {
  if (typeof text !== "string") return 0;
  return text
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0).length;
};

type YearInReviewStats = YearInReviewStatsQuery["yearInReviewStats"];

type CardData = {
  id: string;
  title: string;
  value: string | number;
  description: string | null;
  color: string;
  emoji: string;
};

export default function ShareImageGenerator({
  user,
  stats,
  currentCard,
  activeIndex,
}: {
  user: UserProfile;
  stats: YearInReviewStats;
  currentCard?: CardData;
  activeIndex: number;
}) {
  const shareCardRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const generateImage = async () => {
    if (!shareCardRef.current || !currentCard) return;

    setIsGenerating(true);
    try {
      const canvas = await html2canvas(shareCardRef.current, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
      });

      canvas.toBlob((blob) => {
        if (!blob) return;

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `amani-year-review-2025-${currentCard.id}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, "image/png");
    } catch (error) {
      console.error("Error generating image:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const shareImage = async () => {
    if (!shareCardRef.current || !currentCard) return;

    setIsGenerating(true);
    try {
      const canvas = await html2canvas(shareCardRef.current, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
      });

      canvas.toBlob(async (blob) => {
        if (!blob) return;

        const file = new File([blob], `amani-year-review-2025-${currentCard.id}.png`, {
          type: "image/png",
        });

        if (navigator.share && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              files: [file],
              title: "Мои итоги года в AMANI",
              text: "Посмотри, как прошёл мой год в AMANI! @dance.with.amani",
            });
          } catch (error) {
            console.error("Error sharing:", error);
          }
        } else {
          // Fallback: скачиваем изображение
          generateImage();
        }
      }, "image/png");
    } catch (error) {
      console.error("Error generating image:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  // Определяем, нужно ли использовать SVG для текста
  const isNameCard =
    currentCard?.id === "favoriteAdministrator" ||
    currentCard?.id === "favoriteTeacher" ||
    currentCard?.id === "favoriteDirection" ||
    currentCard?.id === "busiestMonth";
  const wordCount = currentCard ? getWordCount(currentCard.value) : 0;
  const shouldBreakLines = wordCount === 2;
  const textString =
    currentCard && typeof currentCard.value === "string"
      ? currentCard.value
      : currentCard
      ? String(currentCard.value)
      : "";
  const words =
    currentCard && typeof currentCard.value === "string"
      ? currentCard.value.trim().split(/\s+/)
      : currentCard
      ? [String(currentCard.value)]
      : [];

  // Размеры для генератора изображений (больше, чем в карточках)
  const baseFontSize = 180;
  const lineHeight = baseFontSize * 1.2;
  const cardWidth = 1080 - 200; // ширина минус padding
  const svgWidth = cardWidth;
  const svgHeight = shouldBreakLines ? lineHeight * 2 : lineHeight;

  // Вычисляем единый масштаб для всех слов
  const calculateScale = () => {
    if (!currentCard) return 1;

    // Создаем временный элемент для измерения естественной ширины текста
    const tempDiv = document.createElement("div");
    tempDiv.style.position = "absolute";
    tempDiv.style.visibility = "hidden";
    tempDiv.style.whiteSpace = "nowrap";
    tempDiv.style.fontSize = `${baseFontSize}px`;
    tempDiv.style.fontWeight = "bold";
    tempDiv.style.fontFamily = "inherit";

    if (shouldBreakLines && words.length > 0) {
      // Находим самое длинное слово для определения масштаба
      const longestWord = words.reduce((a, b) => (a.length > b.length ? a : b));
      tempDiv.textContent = longestWord;
    } else {
      tempDiv.textContent = textString;
    }

    document.body.appendChild(tempDiv);
    const naturalWidth = tempDiv.offsetWidth;
    document.body.removeChild(tempDiv);

    const availableWidth = svgWidth;
    if (naturalWidth > 0 && availableWidth > 0) {
      const newScale = Math.min(1, availableWidth / naturalWidth);
      return Math.max(0.4, newScale);
    }
    return 1;
  };

  const scale = isNameCard ? calculateScale() : 1;

  if (!currentCard) return null;

  return (
    <Stack gap={3}>
      <Stack direction="row" gap={2} justifyContent="center">
        <Button
          variant="outlined"
          startDecorator={<Download />}
          onClick={generateImage}
          loading={isGenerating}
          disabled={isGenerating}
          sx={{ px: 3, py: 1.5 }}
        >
          <FormattedMessage defaultMessage="Скачать изображение" />
        </Button>
        <Button
          variant="solid"
          startDecorator={<Share />}
          onClick={shareImage}
          loading={isGenerating}
          disabled={isGenerating}
          sx={{ px: 3, py: 1.5 }}
        >
          <FormattedMessage defaultMessage="Поделиться в сторис" />
        </Button>
      </Stack>

      {/* Скрытая карточка для генерации изображения - копия текущей карточки */}
      <Box
        ref={shareCardRef}
        sx={{
          position: "absolute",
          left: "-9999px",
          width: "1080px",
          height: "1920px",
          background: currentCard.color,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "150px 100px",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <Stack
          gap={6}
          sx={{
            width: "100%",
            flex: 1,
            justifyContent: "center",
            alignItems: "flex-start",
            minHeight: 0,
          }}
        >
          <Stack direction="row" gap={3} alignItems="center" sx={{ width: "100%", mb: 3 }}>
            <Typography sx={{ fontSize: "144px", lineHeight: 1, flexShrink: 0 }}>{currentCard.emoji}</Typography>
            <Typography
              sx={{
                color: currentCard.id === "favoriteTeacher" ? "rgba(0, 0, 0, 0.7)" : "rgba(255, 255, 255, 0.95)",
                fontSize: "48px",
                fontWeight: "600",
                flex: 1,
                wordBreak: "break-word",
                overflowWrap: "break-word",
                maxWidth: "100%",
                overflow: "hidden",
                letterSpacing: "1px",
                whiteSpace: "normal",
              }}
            >
              {currentCard.title}
            </Typography>
          </Stack>
          {isNameCard ? (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <svg
                width={svgWidth}
                height={svgHeight}
                style={{
                  overflow: "visible",
                  display: "block",
                }}
              >
                <g transform={`scale(${scale})`}>
                  {(() => {
                    const centerY = svgHeight / 2;
                    const firstLineY = shouldBreakLines ? centerY - lineHeight / 2 : centerY;
                    return shouldBreakLines ? (
                      words.map((word, index) => (
                        <text
                          key={index}
                          x="0"
                          y={firstLineY + lineHeight * index}
                          fontSize={baseFontSize}
                          fontWeight="bold"
                          fill={currentCard.id === "favoriteTeacher" ? "rgba(0, 0, 0, 0.9)" : "white"}
                          dominantBaseline="middle"
                        >
                          {word}
                        </text>
                      ))
                    ) : (
                      <text
                        x="0"
                        y={centerY}
                        fontSize={baseFontSize}
                        fontWeight="bold"
                        fill={currentCard.id === "favoriteTeacher" ? "rgba(0, 0, 0, 0.9)" : "white"}
                        dominantBaseline="middle"
                      >
                        {textString}
                      </text>
                    );
                  })()}
                </g>
              </svg>
            </Box>
          ) : (
            <Typography
              sx={{
                color: currentCard.id === "favoriteTeacher" ? "rgba(0, 0, 0, 0.9)" : "white",
                fontSize: "180px",
                fontWeight: "bold",
                lineHeight: 1.1,
                wordBreak: "break-word",
                overflowWrap: "break-word",
                maxWidth: "100%",
                overflow: "hidden",
                letterSpacing: "-4px",
                whiteSpace: "normal",
              }}
            >
              {currentCard.value}
            </Typography>
          )}
          {currentCard.description && (
            <Typography
              sx={{
                color: currentCard.id === "favoriteTeacher" ? "rgba(0, 0, 0, 0.7)" : "rgba(255, 255, 255, 0.98)",
                fontSize: "48px",
                lineHeight: 1.6,
                wordBreak: "break-word",
                mt: 4,
                letterSpacing: "0.5px",
              }}
            >
              {currentCard.description}
            </Typography>
          )}
        </Stack>
        <Stack
          gap={3}
          sx={{
            width: "100%",
            pt: 5,
            borderTop:
              currentCard.id === "favoriteTeacher"
                ? "3px solid rgba(0, 0, 0, 0.1)"
                : "3px solid rgba(255, 255, 255, 0.3)",
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: "72px",
              textAlign: "center",
              color: currentCard.id === "favoriteTeacher" ? "rgba(0, 0, 0, 0.6)" : "white",
              fontWeight: "600",
              lineHeight: 1.2,
              letterSpacing: "2px",
            }}
          >
            @dance.with.amani
          </Typography>
        </Stack>
      </Box>
    </Stack>
  );
}
